import type {
	PricingEstimationType,
	PricingEstimationTypeFixed,
	PricingEstimationTypeRange,
} from '../../types';
import {
	CENTS_SCALE,
	WIDEN_MAX_FACTOR,
	WIDEN_MIN_FACTOR,
} from './widening-constants';

/**
 * Single source of truth for "this is a Fixed cost the ±10% widening
 * pipeline should operate on." Used by:
 * - `widenFixedPricingEstimation` (skip if not a Fixed positive cost)
 * - `calculate.ts › blocksWidening` (all-Fixed check)
 * - `calculate.ts › accumulateRealEstimate` (sum the real-estimate
 *   contribution only for categories that actually widened)
 *
 * Centralising the guard prevents the three callers from drifting out of
 * agreement when the skip conditions are extended (e.g. NaN handling).
 */
export const isFixedCost = (
	value: PricingEstimationType | null | undefined,
): value is PricingEstimationTypeFixed => {
	if (value === null || value === undefined) return false;
	if (!('cost' in value) || value.cost == null) return false;
	if (value.cost <= 0) return false;
	return true;
};

export const widenFixedPricingEstimation = (
	value: PricingEstimationType,
): PricingEstimationType => {
	if (!isFixedCost(value)) return value;

	const fixed: PricingEstimationTypeFixed = value;
	const widened: PricingEstimationTypeRange = {
		currency: fixed.currency,
		unit: fixed.unit,
		dataSource: fixed.dataSource,
		isFree: fixed.isFree,
		minCost:
			Math.round(WIDEN_MIN_FACTOR * fixed.cost * CENTS_SCALE) / CENTS_SCALE,
		maxCost:
			Math.round(WIDEN_MAX_FACTOR * fixed.cost * CENTS_SCALE) / CENTS_SCALE,
	};
	return widened;
};
